import mapboxgl from 'mapbox-gl'

interface PrintOptions {
  map: mapboxgl.Map
  mapContainer: HTMLDivElement
}

export const captureMapWithMarkers = async ({
  map,
  mapContainer,
}: PrintOptions): Promise<string | null> => {
  return new Promise((resolve) => {
    const captureWhenIdle = () => {
      if (!mapContainer) {
        console.error('Map container not found')
        resolve(null)
        return
      }

      const mapRect = mapContainer.getBoundingClientRect()
      const canvas = document.createElement('canvas')
      canvas.width = mapRect.width
      canvas.height = mapRect.height
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        console.error('Unable to create canvas context')
        resolve(null)
        return
      }

      try {
        // Get the Mapbox canvas directly from the map instance
        const mapCanvas = map.getCanvasContainer().querySelector('canvas')
        if (mapCanvas) {
          ctx.drawImage(mapCanvas, 0, 0, mapRect.width, mapRect.height)
        } else {
          console.error('Map canvas not found')
          resolve(null)
          return
        }
      } catch (error) {
        console.error('Error drawing map canvas:', error)
        resolve(null)
        return
      }

      const finalize = () => {
        resolve(canvas.toDataURL())
      }

      const drawLegend = () => {
        const legendContainer = document.querySelector(
          '[data-legend-container]'
        )
        if (legendContainer) {
          import('html2canvas')
            .then(({ default: html2canvas }) => {
              html2canvas(legendContainer as HTMLElement, {
                backgroundColor: null,
                scale: 1,
              }).then((legendCanvas) => {
                const legendRect = legendContainer.getBoundingClientRect()
                const legendX = canvas.width - legendRect.width - 10
                const legendY = 10
                ctx.drawImage(
                  legendCanvas,
                  legendX,
                  legendY,
                  legendRect.width,
                  legendRect.height
                )
                finalize()
              })
            })
            .catch(() => {
              finalize()
            })
        } else {
          finalize()
        }
      }

      const drawMarkers = () => {
        const markers = mapContainer.querySelectorAll('.mapboxgl-marker')
        let markersProcessed = 0

        if (markers.length === 0) {
          drawLegend()
          return
        }

        markers.forEach((marker) => {
          if (marker instanceof HTMLElement) {
            const markerRect = marker.getBoundingClientRect()
            const x = markerRect.left - mapRect.left
            const y = markerRect.top - mapRect.top
            const markerWidth = markerRect.width
            const markerHeight = markerRect.height
            const radius = 6

            ctx.save()
            ctx.fillStyle = getComputedStyle(marker).backgroundColor
            ctx.beginPath()
            ctx.moveTo(x + radius, y)
            ctx.lineTo(x + markerWidth - radius, y)
            ctx.arc(
              x + markerWidth - radius,
              y + radius,
              radius,
              1.5 * Math.PI,
              2 * Math.PI
            )
            ctx.lineTo(x + markerWidth, y + markerHeight - radius)
            ctx.arc(
              x + markerWidth - radius,
              y + markerHeight - radius,
              radius,
              0,
              0.5 * Math.PI
            )
            ctx.lineTo(x + radius, y + markerHeight)
            ctx.arc(
              x + radius,
              y + markerHeight - radius,
              radius,
              0.5 * Math.PI,
              Math.PI
            )
            ctx.lineTo(x, y + radius)
            ctx.arc(x + radius, y + radius, radius, Math.PI, 1.5 * Math.PI)
            ctx.fill()
            ctx.restore()

            const img = marker.querySelector('img')
            if (img) {
              const drawImage = () => {
                const imgRect = img.getBoundingClientRect()
                const imgX = x + (markerWidth - imgRect.width) / 2
                const imgY = y + (markerHeight - imgRect.height) / 2
                ctx.drawImage(img, imgX, imgY, imgRect.width, imgRect.height)
                markersProcessed++
                if (markersProcessed === markers.length) {
                  drawLegend()
                }
              }
              if (img.complete) {
                drawImage()
              } else {
                img.onload = drawImage
              }
            } else {
              markersProcessed++
              if (markersProcessed === markers.length) {
                drawLegend()
              }
            }
          }
        })
      }

      drawMarkers()
    }

    // Wait for map to be idle, then capture
    if (map.isStyleLoaded()) {
      captureWhenIdle()
    } else {
      map.once('idle', captureWhenIdle)
    }
  })
}

export const downloadMapImage = async (imageUrl: string) => {
  const link = document.createElement('a')
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-')
  link.download = `popety-map-${timestamp}.png`
  link.href = imageUrl
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
